/**
 * `ExtractedSourceViewer` — what a lawyer sees when they open a source
 * the browser cannot render (`.xlsx`, `.pptx`, `.msg`, …).
 *
 * ## The problem this replaces
 *
 * Before this, such a file fell through to `UnsupportedViewer`, which
 * said "pré-visualização não disponível … baixe o arquivo" — and offered
 * no download control (operator-flagged 2026-07-31, on a real `.xlsx`).
 * So the surface was a dead end twice over: it neither showed the file
 * nor let the lawyer get it.
 *
 * And it was a dead end over CONTENT THAT ALREADY EXISTED. The ingest
 * chain runs `office2md` over the upload and writes a `.processed.md`
 * companion holding every sheet as a markdown table, plus a derived
 * heading. The runtime hides that companion from the file tree on
 * purpose (`isInternalFile` — the lawyer's tree shows frozen originals
 * only), which is right; but hidden from the TREE was being read as
 * unavailable to the VIEWER, and nothing ever surfaced it.
 * `document.read_bytes` reaches hidden paths, exactly as
 * `useSourceInfoFile` has always done for the `.info.md` twin.
 *
 * ## What it renders — summary FIRST (mission-ingest ruling 2026-07-31)
 *
 * Not the raw extraction. A 65 KB spreadsheet dump buries the answer, so
 * the lawyer gets, in order:
 *
 *   1. `display.title` — the intake's human title ("Controle de Pagto do
 *      Aluguel"), from the alias overlay the tree already carries.
 *   2. The UPLOADER'S OWN FILENAME ("ACORDOS Buffet-Rizomar.xlsx"). The
 *      frozen original is renamed to a generated slug at ingest, so this
 *      is the only string tying what they see to what they uploaded —
 *      and not having it was half the operator's complaint.
 *   3. `display.description` — authored as lawyer-facing abstract text by
 *      the intake's `derive-display-name`, so it is rendered AS the
 *      summary rather than the SPA inventing one.
 *   4. The sections the extraction found (a spreadsheet's sheet names).
 *   5. Provenance: which converter produced this, and how confident.
 *
 * The full extraction sits behind an explicit "ver extração completa".
 *
 * ## What it deliberately does NOT do
 *
 * A `.processed.md` is written even when extraction FAILED, carrying
 * `extraction.status` to say so. Rendering that as though it were the
 * document would show a lawyer a stub and call it their evidence — so a
 * failed extraction routes to a warning and never to the reader.
 *
 * And an ABSENT companion on a `sources/*` original is an ANOMALY, not a
 * normal state: the intake pipelines are catch-and-stub and always write
 * one, so its absence means the freeze and the extract disagree (what
 * Part H's census exists to repair). It is surfaced as a fault rather
 * than as a bland "no preview available" — the reassuring wording would
 * hide a real inconsistency in the lawyer's evidence set.
 */

import { Suspense, lazy, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  Loader2,
} from "lucide-react";
import { basename, type FileRef } from "@/stores/useWorkspaceFileStore";
import {
  extractionPathForSource,
  extractionSucceeded,
  useSourceExtraction,
  type SourceExtraction,
} from "@/hooks/useSourceExtraction";
import { useFileDownload } from "@/hooks/useFileDownload";
import { useWorkspaceAliasMap } from "@/hooks/useWorkspaceAliasMap";
import { ViewerInfoBar, type ViewerDownload } from "./ViewerInfoBar";

const LazyMarkdownHtmlViewer = lazy(() =>
  import("./MarkdownHtmlViewer").then((m) => ({
    default: m.MarkdownHtmlViewer,
  })),
);

interface ExtractedSourceViewerProps {
  file: FileRef;
  onClose?: () => void;
}

/** File extension, lowercased, without the dot. */
function extensionOf(path: string): string {
  const dot = path.lastIndexOf(".");
  return dot >= 0 ? path.slice(dot + 1).toLowerCase() : "";
}

/**
 * The name the browser should save as.
 *
 * Prefers the uploader's OWN filename from the companion frontmatter —
 * they recognise "ACORDOS Buffet-Rizomar.xlsx"; they do not recognise
 * `controle-aluguel-fantastikday__documento__20260731-044654.xlsx`.
 * Falls back to the frozen basename, which is ugly but at least keeps a
 * working extension. Never the display TITLE: it carries no extension,
 * and a file saved without one is a file the OS cannot open.
 */
function downloadNameFor(file: FileRef, extraction: SourceExtraction | null) {
  return extraction?.originalName ?? basename(file.path);
}

/**
 * Section headings the extraction found — a spreadsheet's sheet names.
 *
 * Exported for the colocated test: the skip rule below is a judgement
 * call over open data and deserves to be pinned.
 *
 * The `office2md` output opens with a generic `## Conteúdo` wrapper that
 * names nothing, so it is dropped — but ONLY as the leading heading. A
 * spreadsheet is free to contain a sheet genuinely called "Conteúdo",
 * and silently deleting every heading matching a hard-coded word would
 * be a closed vocabulary imposed on open data.
 */
export function sectionsOf(body: string): string[] {
  const all = [...body.matchAll(/^##\s+(.+)$/gm)]
    .map((m) => m[1].trim())
    .filter(Boolean);
  if (all.length > 0 && all[0].toLowerCase() === "conteúdo") all.shift();
  return all;
}

export function ExtractedSourceViewer({
  file,
  onClose,
}: ExtractedSourceViewerProps) {
  const { t } = useTranslation();
  const { state, extraction, error } = useSourceExtraction(file);
  // `intent: "extraction"` (the case-files card's "Ver extração") mounts with
  // the full extraction already open; the default stays summary-first, which
  // is what stops a 65 KB spreadsheet dump from burying the answer.
  const [showFull, setShowFull] = useState(file.intent === "extraction");

  // `display.title` / `display.description` — the intake's lawyer-facing
  // text, already carried by the tree's alias overlay, so reading it here
  // costs no extra round trip.
  const aliasMap = useWorkspaceAliasMap();
  const entry = aliasMap.byPath.get(file.path) ?? null;

  const filename = downloadNameFor(file, extraction);
  const dl = useFileDownload(file.path, filename);
  const ext = extensionOf(file.path);
  const download: ViewerDownload = {
    filename,
    onDownload: dl.request,
    pending: dl.pending,
  };

  const sections = useMemo(
    () => (extraction ? sectionsOf(extraction.body) : []),
    [extraction],
  );

  const usable =
    state === "present" && extraction !== null && extractionSucceeded(extraction);

  if (usable && extraction) {
    const companionPath = extractionPathForSource(file);
    return (
      <div
        data-testid="workspace-file-pane-viewer-extracted"
        data-file-extension={ext}
        data-body-expanded={showFull || undefined}
        // Same attribute the fallback branch exposes, so a probe can read
        // the resolved state off EITHER surface without first having to
        // know which one it landed on.
        data-extraction-state={state}
        className="flex h-full min-h-0 flex-col"
      >
        <Suspense
          fallback={<Centered spin message={t("workspace.filePane.viewer.loading")} />}
        >
          <LazyMarkdownHtmlViewer
            // Synthetic ref: the BODY comes from the companion, but the
            // label stays the original's — the lawyer never addresses the
            // companion, and its slug is not a name they would recognise.
            file={{
              path: companionPath ?? file.path,
              title: entry?.title ?? file.title ?? extraction.originalName,
            }}
            onClose={onClose}
            download={download}
            bodyHidden={!showFull}
            // Untrusted extracted content — see the prop's docblock.
            allowRawHtml={false}
            banner={
              <ExtractionSummary
                extraction={extraction}
                ext={ext}
                title={entry?.title}
                description={entry?.description}
                sections={sections}
                expanded={showFull}
                onToggle={() => setShowFull((v) => !v)}
              />
            }
          />
        </Suspense>
      </div>
    );
  }

  // ── Everything else: honest states, each with the download control the
  // old message told the lawyer to use but never provided. ──────────────
  const fault =
    state === "unreadable" ||
    state === "absent" ||
    (state === "present" && !usable);

  const message =
    state === "loading"
      ? t("workspace.filePane.viewer.loading")
      : state === "unreadable"
        ? t("workspace.filePane.viewer.extraction.unreadable", {
            reason: error?.message ?? "",
          })
        : state === "absent"
          ? // NOT "no preview available": the pipelines always write a
            // companion, so its absence is an inconsistency worth naming.
            t("workspace.filePane.viewer.extraction.missing")
          : state === "present"
            ? t("workspace.filePane.viewer.extraction.failed", {
                status: extraction?.extraction.status ?? "",
              })
            : t("workspace.filePane.viewer.unsupported", {
                ext: ext || t("workspace.filePane.viewer.unsupportedNoExt"),
              });

  return (
    <div
      // Testid preserved from `UnsupportedViewer` — this is still the
      // no-preview surface, and anything pointing at it keeps working.
      data-testid="workspace-file-pane-viewer-unsupported"
      data-file-extension={ext}
      data-extraction-state={state}
      className="flex h-full min-h-0 flex-col"
    >
      <ViewerInfoBar file={file} onClose={onClose} download={download} />
      <Centered spin={state === "loading"} warn={fault} message={message} />
      {dl.isError && (
        <p
          data-testid="workspace-file-pane-download-error"
          className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-center text-[11px] text-amber-800 dark:border-amber-900/60 dark:bg-amber-900/20 dark:text-amber-200"
        >
          {t("workspace.filePane.viewer.downloadFailed", {
            reason: dl.error?.message ?? "",
          })}
        </p>
      )}
    </div>
  );
}

/**
 * The summary card — what the lawyer reads instead of six raw tables.
 *
 * Everything here is either the intake's own lawyer-facing text or a
 * verbatim machine fact. Nothing is synthesised in the SPA: a summary
 * this component invented would be a fourth account of the document,
 * competing with the three that already exist.
 */
function ExtractionSummary({
  extraction,
  ext,
  title,
  description,
  sections,
  expanded,
  onToggle,
}: {
  extraction: SourceExtraction;
  ext: string;
  title?: string;
  description?: string;
  sections: string[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const pct =
    extraction.extraction.confidence === undefined
      ? null
      : Math.round(extraction.extraction.confidence * 100);
  const heading = title ?? extraction.h1;

  return (
    <section
      data-testid="workspace-file-pane-extraction-summary"
      className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-coffee-800 dark:bg-coffee-900/50"
    >
      {heading && (
        <h2 className="text-[15px] font-medium text-slate-800 dark:text-cream-100">
          {heading}
        </h2>
      )}

      <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-slate-500 dark:text-cream-500">
        <FileSpreadsheet size={12} className="shrink-0 opacity-70" aria-hidden />
        {extraction.originalName && (
          <span
            data-testid="workspace-file-pane-extraction-original"
            className="font-medium text-slate-700 dark:text-cream-300"
            title={extraction.originalName}
          >
            {extraction.originalName}
          </span>
        )}
        {ext && <span className="uppercase opacity-70">{ext}</span>}
      </p>

      {description && (
        <p
          data-testid="workspace-file-pane-extraction-description"
          className="mt-2 max-w-3xl text-[13px] leading-relaxed text-slate-700 dark:text-cream-300"
        >
          {description}
        </p>
      )}

      {sections.length > 0 && (
        <p
          data-testid="workspace-file-pane-extraction-sections"
          className="mt-2 text-[11px] text-slate-500 dark:text-cream-500"
        >
          {t("workspace.filePane.viewer.extraction.sections", {
            count: sections.length,
          })}{" "}
          <span className="text-slate-600 dark:text-cream-400">
            {sections.join(" · ")}
          </span>
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid="workspace-file-pane-extraction-toggle"
          onClick={onToggle}
          aria-expanded={expanded}
          className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-700 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:border-coffee-700 dark:text-cream-300 dark:hover:bg-coffee-800/70"
        >
          {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          {expanded
            ? t("workspace.filePane.viewer.extraction.hideFull")
            : t("workspace.filePane.viewer.extraction.showFull")}
        </button>

        {extraction.extraction.method && (
          <span className="text-[11px] text-slate-400 dark:text-cream-500">
            {t("workspace.filePane.viewer.extraction.method", {
              method: extraction.extraction.method,
            })}
            {pct !== null &&
              ` · ${t("workspace.filePane.viewer.extraction.confidence", { pct })}`}
          </span>
        )}
      </div>
    </section>
  );
}

function Centered({
  message,
  spin,
  warn,
}: {
  message: string;
  spin?: boolean;
  warn?: boolean;
}) {
  const Icon = spin ? Loader2 : warn ? AlertTriangle : FileSpreadsheet;
  return (
    <div
      data-viewer-state={spin ? "loading" : warn ? "error" : "empty"}
      className="flex h-full min-h-0 flex-col items-center justify-center gap-2 p-6 text-sm text-slate-500 dark:text-slate-400"
    >
      <Icon size={20} className={spin ? "animate-spin" : undefined} />
      <p className="max-w-md text-center text-xs">{message}</p>
    </div>
  );
}
